import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateMain } from '@hero/core';
import type { Agreement } from '@hero/schema';
import { CalendarIcon, FileTextIcon, RefreshIcon, TruckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { AgreementStatusBadge } from '../Status/AgreementStatusBadge';
import { formatPaymentTermsShort } from '../../../app/PaymentTerms';
import { getAgreementCounterparty } from '../utils';

export interface AgreementSummaryCardProps {
  agreement: Agreement;
  /**
   * Display name used when the agreement's `parties[]` snapshot is
   * missing a counterparty (legacy single-org docs). Typically the
   * Client's `name` from the calling surface.
   */
  fallbackName: string;
  /** Current org id — used to determine which `parties[]` entry is the counterparty. */
  orgId?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * Dense agreement summary card.
 *
 * Layout (top → bottom):
 *   1. Title row — agreement title + status badge.
 *   2. Counterparty + type subtitle.
 *   3. Single meta row that mixes the colored payment chips
 *      (`Invoice: Monthly`, `Pay: 30 days`) with the muted date /
 *      auto-renew / monthly-volume items. Keeping payment inline
 *      with the rest of the meta saves a whole vertical line over
 *      the previous "dedicated payment row" treatment; the indigo /
 *      emerald backgrounds are enough to make the values pop next
 *      to the muted gray entries.
 *
 * The compact value-only format ("Monthly", "30 days", "Manual") is
 * shared with any future list/row surfaces via
 * `formatPaymentTermsShort`. The full-sentence preview lives on the
 * form (`PaymentTermsFields`) where the user is composing the terms
 * and benefits from the long, unambiguous wording.
 *
 * Used by:
 *   - the per-Client workspace Info tab as the contract envelope
 *     section card (one open agreement per `(org, client)` pair by
 *     backend rule).
 *   - the legacy Client workspace Agreements tab (URL-only, hidden
 *     from the tab strip) for the same client.
 */
export function AgreementSummaryCard({
  agreement,
  fallbackName,
  orgId,
  onClick,
  className,
}: AgreementSummaryCardProps) {
  const { t } = useTranslation(['m-agreement']);
  const cp = getAgreementCounterparty(agreement, orgId);
  const start = agreement.dates?.start;
  const end = agreement.dates?.end;
  const autoRenew = !!agreement.dates?.autoRenew;
  const monthlyVolume = agreement.monthlyVehicleVolume ?? 0;
  const paymentShort = formatPaymentTermsShort(t, agreement.terms?.payment);

  const formatDate = (d: Date | string | undefined) =>
    d ? formatDateMain({ date: d as never, format: 'date' }) : '—';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-start rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors',
        className,
      )}>
      <div className="flex items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
          <FileTextIcon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {agreement.title || cp?.name || fallbackName}
              </div>
              <div className="mt-0.5 text-[11px] text-gray-500 truncate">
                {cp?.name ?? fallbackName} ·{' '}
                {t(`type.${agreement.type}`, {
                  defaultValue: agreement.type,
                })}
              </div>
            </div>
            <AgreementStatusBadge status={agreement.status} className="flex-shrink-0" />
          </div>

          {/* Single meta row that mixes the colored payment chips
              with the muted date / auto-renew / monthly-volume
              items. Keeping them on one wrapping flex line saves
              vertical height vs a dedicated payment row above; the
              backgrounds (indigo / emerald) keep the payment values
              legible against the muted gray meta items even in the
              shared row. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-600">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="size-3 text-gray-400" />
              <span className="text-gray-500">
                {formatDate(start)} – {formatDate(end)}
              </span>
            </span>
            {autoRenew ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <RefreshIcon className="size-3" />
                {t('card.auto_renew', { defaultValue: 'Auto-renew' })}
              </span>
            ) : null}
            {monthlyVolume > 0 ? (
              <span className="inline-flex items-center gap-1">
                <TruckIcon className="size-3 text-gray-400" />
                <span className="text-gray-500">
                  {t('card.monthly_volume', {
                    count: monthlyVolume,
                    defaultValue: '{{count}} / mo',
                  })}
                </span>
              </span>
            ) : null}
            {paymentShort.invoice ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-indigo-700">
                <span className="font-medium">
                  {t('card.payment.invoice_label', { defaultValue: 'Invoice' })}:
                </span>
                <span>{paymentShort.invoice}</span>
              </span>
            ) : null}
            {paymentShort.pay ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                <span className="font-medium">
                  {t('card.payment.pay_label', { defaultValue: 'Pay' })}:
                </span>
                <span>{paymentShort.pay}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
}
